import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useOrders } from '../context/OrderContext';
import { OrderResponse } from '../hooks/useOrders';
import {
  OrderStatus,
  PaymentStatus,
  ShipmentStatus,
} from '../components/StatusChips';
import api from '../services/api';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input, InputGroup, InputAddon, Select } from '../components/ui/Input';
import { Field } from '../components/ui/Field';
import { Badge } from '../components/ui/Badge';
import { Stat } from '../components/ui/Stat';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';
import { AlertDialog } from '../components/ui/radix/AlertDialog';
import ImportOrdersWizard from '../features/orderImport/ImportOrdersWizard';

const getOrderStatusValues = (
  order: OrderResponse
): { orderStatus: OrderStatus; paymentStatus: PaymentStatus; shipmentStatus: ShipmentStatus } => {
  let orderStatus: OrderStatus = 'processing';
  let paymentStatus: PaymentStatus = 'pending';
  let shipmentStatus: ShipmentStatus = 'pending';
  if (!order.order_status || !order.payment_status || !order.shipment_status) {
    switch (order.status) {
      case 'pending':
        orderStatus = 'created';
        paymentStatus = 'pending';
        shipmentStatus = 'pending';
        break;
      case 'processing':
        orderStatus = 'processing';
        paymentStatus = 'paid';
        shipmentStatus = 'pending';
        break;
      case 'shipped':
        orderStatus = 'processing';
        paymentStatus = 'paid';
        shipmentStatus = 'in_transit';
        break;
      case 'delivered':
        orderStatus = 'completed';
        paymentStatus = 'paid';
        shipmentStatus = 'delivered';
        break;
      case 'cancelled':
        orderStatus = 'cancelled';
        paymentStatus = order.paid_at ? 'refunded' : 'failed';
        shipmentStatus = 'cancelled';
        break;
    }
  }
  return {
    orderStatus: order.order_status || orderStatus,
    paymentStatus: order.payment_status || paymentStatus,
    shipmentStatus: order.shipment_status || shipmentStatus,
  };
};

const shipmentTone = (s: ShipmentStatus): 'ok' | 'info' | 'warn' | 'err' | 'default' => {
  switch (s) {
    case 'delivered': return 'ok';
    case 'in_transit':
    case 'out_for_delivery':
    case 'ready':
    case 'scheduled_for_pickup': return 'info';
    case 'pending': return 'warn';
    case 'exception': return 'err';
    case 'cancelled': return 'err';
    default: return 'default';
  }
};

const shipmentLabel = (s: ShipmentStatus): string =>
  s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const formatAddress = (address: any): string => {
  if (!address) return 'N/A';
  const city = address.city?.name || address.city_other || '';
  const country = address.country?.name || '';
  return [address.contactName, [city, country].filter(Boolean).join(', ')]
    .filter(Boolean)
    .join(' · ');
};

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [shipmentStatusFilter, setShipmentStatusFilter] = useState<ShipmentStatus | 'all'>('all');
  const [dateSort, setDateSort] = useState<'asc' | 'desc'>('desc');
  const [tab, setTab] = useState<'all' | 'active' | 'delivered' | 'exception'>('all');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<OrderResponse | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const {
    ordersQuery,
    cancelOrder,
    isCancelingOrder,
    downloadLabel,
    isDownloadingLabel,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    setFilters,
  } = useOrders();

  useEffect(() => {
    const tabFilter: { order_status?: OrderStatus; shipment_status?: ShipmentStatus } = {};
    if (tab === 'active') tabFilter.shipment_status = 'in_transit';
    if (tab === 'delivered') tabFilter.shipment_status = 'delivered';
    if (tab === 'exception') tabFilter.shipment_status = 'exception';

    setFilters({
      search: searchQuery,
      order_status: orderStatusFilter !== 'all' ? orderStatusFilter : tabFilter.order_status,
      shipment_status:
        shipmentStatusFilter !== 'all' ? shipmentStatusFilter : tabFilter.shipment_status,
      sort_date: dateSort,
    });
  }, [searchQuery, orderStatusFilter, shipmentStatusFilter, dateSort, tab, setFilters]);

  const handleConfirmCancelOrder = async () => {
    if (!orderToCancel) return;
    try {
      await cancelOrder(orderToCancel.id);
    } finally {
      setCancelDialogOpen(false);
      setOrderToCancel(null);
    }
  };

  const handleDownloadReceipt = async (orderId: number) => {
    setDownloadingId(orderId);
    try {
      const response = await api.get(`/orders/${orderId}/receipt/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `order-receipt-${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download receipt. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadLabel = async (orderId: number) => {
    try {
      const labelData = await downloadLabel(orderId);
      if (labelData.status === 'success' && labelData.data?.label_content) {
        const byteCharacters = atob(labelData.data.label_content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `shipping-label-${labelData.data.tracking_number || orderId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Invalid label data format');
      }
    } catch {
      alert('Failed to download shipping label. Please try again.');
    }
  };

  const canCancelOrder = (order: OrderResponse) =>
    ['draft', 'created', 'processing'].includes(order.order_status || order.status) &&
    ['pending', 'ready', 'scheduled_for_pickup'].includes(order.shipment_status || order.status);

  const orders = ordersQuery.data?.data || [];
  const totalOrders = ordersQuery.data?.meta.total || 0;

  const stats = {
    active: orders.filter((o) =>
      ['in_transit', 'out_for_delivery'].includes(o.shipment_status || '')
    ).length,
    delivered: orders.filter((o) => o.shipment_status === 'delivered').length,
    exception: orders.filter((o) => o.shipment_status === 'exception').length,
    spent: orders.reduce((sum, o) => sum + Number((o as any).total_amount || 0), 0),
  };

  return (
    <Page width="wide">
      <PageHead>
        <div>
          <PageTitle>Shipments</PageTitle>
          <PageSub>{totalOrders} total · {stats.active} active</PageSub>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => ordersQuery.refetch()} title="Refresh">
            <Icon name="refresh" size={14} />
            <span>Refresh</span>
          </Button>
          <Button onClick={() => setImportOpen(true)} title="Import orders from a spreadsheet">
            <Icon name="file" size={14} />
            <span>Import from sheet</span>
          </Button>
          <Button variant="blue" onClick={() => navigate('/shipping')}>
            <Icon name="plus" size={14} />
            <span>New shipment</span>
          </Button>
        </div>
      </PageHead>

      <div className="grid-stats" style={{ marginBottom: 24 }}>
        <Stat label="Active" value={stats.active} />
        <Stat label="Delivered" value={stats.delivered} />
        <Stat label="Issues" value={stats.exception} />
        <Stat
          label="Spent (visible)"
          value={`$${stats.spent.toFixed(2)}`}
          valueClassName="mono"
        />
      </div>

      <Card>
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div className="tabs" style={{ border: 'none' }}>
            {(['all', 'active', 'delivered', 'exception'] as const).map((t) => (
              <button
                key={t}
                type="button"
                className={`tab ${tab === t ? 'active' : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'all' ? 'All' : t === 'exception' ? 'Issues' : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <InputGroup style={{ minWidth: 240 }}>
              <InputAddon><Icon name="search" size={13} /></InputAddon>
              <Input
                placeholder="Search by order #, tracking #, or name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
            <Field>
              <Select
                value={orderStatusFilter}
                onChange={(e) =>
                  setOrderStatusFilter(e.target.value as OrderStatus | 'all')
                }
              >
                <option value="all">All order statuses</option>
                <option value="draft">Draft</option>
                <option value="created">Created</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="failed">Failed</option>
              </Select>
            </Field>
            <Field>
              <Select
                value={shipmentStatusFilter}
                onChange={(e) =>
                  setShipmentStatusFilter(e.target.value as ShipmentStatus | 'all')
                }
              >
                <option value="all">All shipment statuses</option>
                <option value="pending">Pending</option>
                <option value="ready">Ready</option>
                <option value="in_transit">In transit</option>
                <option value="out_for_delivery">Out for delivery</option>
                <option value="delivered">Delivered</option>
                <option value="exception">Exception</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </Field>
            <Field>
              <Select value={dateSort} onChange={(e) => setDateSort(e.target.value as 'asc' | 'desc')}>
                <option value="desc">Newest first</option>
                <option value="asc">Oldest first</option>
              </Select>
            </Field>
          </div>
        </div>

        {ordersQuery.isError && (
          <div style={{ padding: 16 }}>
            <Callout tone="warn">
              <Icon name="alertCircle" size={16} />
              <span>
                Failed to load orders: {ordersQuery.error?.message || 'Unknown error'}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={() => ordersQuery.refetch()}
                style={{ marginInlineStart: 'auto' }}
              >
                Retry
              </button>
            </Callout>
          </div>
        )}

        <table className="table">
          <thead>
            <tr>
              <th>Order #</th>
              <th>Date</th>
              <th>Route</th>
              <th>Service</th>
              <th>Status</th>
              <th>Tracking</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ordersQuery.isLoading && (
              <tr>
                <td colSpan={7}>
                  <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    Loading shipments…
                  </div>
                </td>
              </tr>
            )}
            {!ordersQuery.isLoading && orders.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <div className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    <Icon name="package" size={28} color="var(--ink-4)" />
                    <div style={{ marginTop: 8, fontWeight: 600 }}>No orders found</div>
                    <div style={{ fontSize: 13 }}>Try adjusting your search or filter criteria</div>
                  </div>
                </td>
              </tr>
            )}
            {!ordersQuery.isLoading &&
              orders.map((order) => {
                const { shipmentStatus } = getOrderStatusValues(order);
                return (
                  <tr
                    key={order.id}
                    className="clickable"
                    onClick={() => navigate(`/orders/${order.id}`)}
                  >
                    <td>
                      <span className="mono" style={{ fontSize: 13 }}>
                        {order.order_number || order.id}
                      </span>
                      {order.is_document && (
                        <Badge style={{ marginInlineStart: 6, fontSize: 10 }}>Document</Badge>
                      )}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      <div>
                        {(() => {
                          try {
                            return format(parseISO(order.created_at), 'MMM d, yyyy');
                          } catch {
                            return 'Invalid date';
                          }
                        })()}
                      </div>
                      {order.reference_number && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          {order.reference_number}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 13 }}>
                      <div>{formatAddress(order.from_address)}</div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        → {formatAddress(order.to_address)}
                      </div>
                    </td>
                    <td>{order.service}</td>
                    <td>
                      <Badge tone={shipmentTone(shipmentStatus)} dot>
                        {shipmentLabel(shipmentStatus)}
                      </Badge>
                    </td>
                    <td>
                      {order.tracking_number ? (
                        <span className="mono" style={{ fontSize: 12 }}>
                          {order.tracking_number}
                        </span>
                      ) : (
                        <span className="muted" style={{ fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td
                      style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => handleDownloadReceipt(order.id)}
                        disabled={downloadingId === order.id}
                        title="Download receipt"
                      >
                        <Icon name="receipt" size={13} />
                      </Button>
                      {order.tracking_number && (
                        <>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => {
                              const provider = order.carrier || order.provider;
                              const url = provider
                                ? `/track/${order.tracking_number}?provider=${provider.toLowerCase()}`
                                : `/track/${order.tracking_number}`;
                              navigate(url);
                            }}
                            title="Track shipment"
                          >
                            <Icon name="route" size={13} />
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => handleDownloadLabel(order.id)}
                            disabled={isDownloadingLabel}
                            title="Download label"
                          >
                            <Icon name="download" size={13} />
                          </Button>
                        </>
                      )}
                      {canCancelOrder(order) && (
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => {
                            setOrderToCancel(order);
                            setCancelDialogOpen(true);
                          }}
                          disabled={isCancelingOrder}
                          title="Cancel order"
                        >
                          <Icon name="x" size={13} />
                        </Button>
                      )}
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => navigate(`/orders/${order.id}`)}
                        title="View"
                      >
                        <Icon name="chevronRight" size={13} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        <div
          style={{
            padding: 14,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid var(--line)',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div className="muted" style={{ fontSize: 13 }}>
            {orders.length} shown · {totalOrders} total
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Field>
              <Select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
                style={{ width: 90 }}
              >
                {[5, 10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              <Icon name="chevronLeft" size={14} />
            </Button>
            <span className="mono" style={{ fontSize: 13 }}>Page {currentPage}</span>
            <Button
              size="sm"
              disabled={orders.length < pageSize}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              <Icon name="chevronRight" size={14} />
            </Button>
          </div>
        </div>
      </Card>

      <AlertDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title="Cancel order"
        description={
          orderToCancel
            ? `Cancel order #${orderToCancel.order_number || orderToCancel.id}? This action cannot be undone.${
                orderToCancel.payment_status === 'paid'
                  ? ' A refund will be processed according to our refund policy.'
                  : ''
              }`
            : ''
        }
        confirmLabel={isCancelingOrder ? 'Cancelling…' : 'Cancel order'}
        cancelLabel="Keep order"
        onConfirm={handleConfirmCancelOrder}
        destructive
      />

      {importOpen && (
        <ImportOrdersWizard
          onClose={(didCreate) => {
            setImportOpen(false);
            if (didCreate) ordersQuery.refetch();
          }}
        />
      )}
    </Page>
  );
};

export default OrdersPage;
